import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useProducts } from '@/contexts/ProductsContext';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/contexts/ToastContext';
import { fetchPublicStock, stockForSlug, type StockBySku } from '@/lib/publicStock';
import { applyDiscountToPrice } from '@/lib/discountApi';
import { trackAddToCart } from '@/lib/tracking';
import SharedNavbar from '@/components/feature/SharedNavbar';
import { Seo } from '@/components/Seo';
import SharedFooter from '@/components/feature/SharedFooter';
import { useSiteDesign } from '@/hooks/useSiteDesign';
import HomeV2 from '@/pages/home/v2/HomeV2';
import StockBadge from '@/components/feature/StockBadge';

const stepColor: Record<string, string> = {
  OPEN: '#1B3A5C',
  TREAT: '#7B1113',
  LOCK: '#2D6A4F',
};

export default function ProductsPage() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { products, routineSet, autoDiscount } = useProducts();
  const { addToCart, openCart } = useCart();
  const { showToast } = useToast();
  const [stock, setStock] = useState<StockBySku>({});
  const { isNew } = useSiteDesign();

  useEffect(() => {
    fetchPublicStock().then(setStock);
  }, []);

  const handleAddSet = () => {
    addToCart('3-step-set', 1);
    if (routineSet) {
      trackAddToCart({ id: '3-step-set', name: routineSet.name, price: Number(routineSet.price.replace(/[^0-9.]/g, '')), quantity: 1 });
    }
    showToast(
      t('cart.added', 'Added to Cart ✓'),
      t('cart.viewCart', 'View Cart →'),
      openCart,
    );
  };

  if (isNew)
    return (
      <>
        <Seo pageKey="products" />
        <SharedNavbar />
        <main className="w-full min-h-screen bg-white">
          <div className="v2 v2-pagehead" style={{ paddingTop: 'var(--v2h-h, 64px)' }}>
            <div className="wrap">
              <span className="lbl">{t('products.hero.label')}</span>
              <h1>{t('products.hero.heading')}</h1>
              <p>{t('products.hero.subtitle')}</p>
            </div>
          </div>
          <HomeV2 only={['shop']} />
          <SharedFooter />
        </main>
      </>
    );

  return (
    <>
      <Seo pageKey="products" />
      <SharedNavbar />
      <main className="w-full min-h-screen bg-white font-sans">
        <div className="pt-24 pb-20 px-6 md:px-12 lg:px-20">
          <div className="max-w-7xl mx-auto">
            {/* Hero header */}
            <div className="flex flex-col gap-4 mb-12 md:mb-16">
              <div className="flex items-center gap-3">
                <div className="w-8 h-px bg-[#7B1113]" />
                <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B1113] font-medium">
                  {t('products.hero.label')}
                </span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#2A2A2A] leading-[1.1]">
                {t('products.hero.heading')}
              </h1>
              <p className="text-sm text-[#8A8A8A] font-light max-w-lg">
                {t('products.hero.subtitle')}
              </p>

              {/* 3-Step Set CTA — price/savings pulled live from the real
                  SET-3STEP bundle (routineSet) so this never drifts from
                  what checkout actually charges; the button adds it to the
                  cart directly instead of just reloading this page.
                  Side-by-side layout: image panel scales as a % of the card
                  (not a fixed px width) so it actually grows on wide
                  screens instead of leaving the text panel's flex-1 to
                  balloon into empty dark space. The step row below the CTA
                  reuses the real per-product step/name/color already used
                  for the grid cards below — it fills the remaining width
                  with what's actually in the set, not decoration. */}
              {routineSet && (
                <div className="mt-2 rounded-2xl overflow-hidden bg-[#1A1A1A] flex flex-col sm:flex-row sm:min-h-72">
                  <Link
                    to={`${prefix}/3-step-treatment`}
                    className="group w-full h-[7.5rem] sm:h-auto sm:w-[34%] lg:w-[30%] flex-shrink-0 bg-[#F0EFED] overflow-hidden block cursor-pointer"
                  >
                    <img src={routineSet.image} alt={routineSet.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                  </Link>
                  <div className="flex-1 min-w-0 p-2.5 sm:p-6 lg:p-10 flex flex-col justify-between gap-1 sm:gap-4">
                    <div className="flex flex-col items-center sm:items-start gap-0.5 sm:gap-2 text-center sm:text-left">
                      <span className="hidden sm:block text-[11px] tracking-[0.2em] uppercase text-white/40 font-medium">{t('products.fullRoutine')}</span>
                      <Link
                        to={`${prefix}/3-step-treatment`}
                        className="font-serif text-base sm:text-2xl md:text-3xl font-bold text-white leading-tight hover:text-white/80 transition-colors cursor-pointer"
                      >
                        {routineSet.name}
                      </Link>
                      {routineSet.description && (
                        <p className="hidden sm:block text-sm text-white/50 font-light leading-relaxed max-w-2xl line-clamp-2">
                          {routineSet.description}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-center sm:items-stretch sm:flex-row sm:flex-wrap sm:justify-between gap-1 sm:gap-4">
                      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-2 sm:gap-x-3 gap-y-1">
                        <span className="font-serif text-base sm:text-xl lg:text-2xl font-semibold text-white">{routineSet.price}</span>
                        <span className="text-[11px] sm:text-xs text-white/35 line-through">{routineSet.originalPrice}</span>
                        <span className="text-[10px] sm:text-[10.5px] font-semibold tracking-wide text-[#4ADE80] bg-[#2D6A4F]/20 px-2 py-0.5 rounded-full">
                          SAVE {routineSet.savings}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddSet}
                        className="w-full sm:w-auto px-5 lg:px-7 py-2 sm:py-3 bg-[#7B1113] text-white text-[11px] lg:text-xs tracking-[0.1em] lg:tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center flex-shrink-0 whitespace-nowrap"
                      >
                        {t('products.hero.cta')}
                      </button>
                    </div>

                    {products.length > 0 && (
                      <div className="hidden lg:flex items-center justify-between gap-4 pt-4 border-t border-white/10">
                        {products.map((p) => (
                          <div key={p.id} className="flex items-center gap-2 min-w-0">
                            <span
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: stepColor[p.stepKeyword] || '#7B1113' }}
                            />
                            <span className="text-[11px] text-white/70 font-semibold tracking-wide flex-shrink-0">{p.stepKeyword}</span>
                            <span className="text-[11px] text-white/30 flex-shrink-0">·</span>
                            <span className="text-[11px] text-white/50 font-light truncate">{p.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((product) => {
                const numericPrice = Number(product.price.replace(/[^0-9.]/g, ''));
                const discountedPrice = autoDiscount ? applyDiscountToPrice(numericPrice, autoDiscount, product.sku) : null;
                return (
                <Link
                  key={product.id}
                  to={`${prefix}/products/${product.id}`}
                  className="group flex flex-col rounded-2xl overflow-hidden bg-[#F0EFED] hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="relative w-full aspect-square overflow-hidden flex items-center justify-center">
                    <img
                      src={product.image}
                      alt={product.imageAlt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div
                      className="absolute top-4 left-4 text-[9px] tracking-[0.1em] uppercase px-2.5 py-1 rounded-full text-white font-medium"
                      style={{ backgroundColor: stepColor[product.stepKeyword] || '#7B1113' }}
                    >
                      {product.stepKeyword}
                    </div>
                    <StockBadge qty={stockForSlug(product.id, stock)} className="absolute top-4 right-4" onlyOutOfStock />
                  </div>

                  <div className="flex flex-col gap-2 p-6 pt-2">
                    <h3 className="font-serif text-lg font-semibold text-[#2A2A2A] group-hover:text-[#7B1113] transition-colors">
                      {product.name}
                    </h3>
                    <span className="text-xs text-[#8A8A8A] font-light">{product.subtitle}</span>
                    {discountedPrice !== null ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#7B1113]">৳{discountedPrice.toLocaleString()}</span>
                        <span className="text-xs text-[#8A8A8A] font-light line-through">{product.price}</span>
                      </div>
                    ) : (
                      <span className="text-sm font-medium text-[#2A2A2A]">{product.price}</span>
                    )}
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      <SharedFooter />
    </>
  );
}